import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Bot, Sliders, Thermometer, Zap, Brain, CheckCircle, RotateCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

const MODELS = [
    { id: 'gpt-4', name: 'GPT-4 Turbo', speed: 'Slow', quality: 'Best', cost: '$$$', color: 'bg-purple-500' },
    { id: 'gpt-3.5', name: 'GPT-3.5 Turbo', speed: 'Fast', quality: 'Good', cost: '$', color: 'bg-blue-500' },
    { id: 'claude', name: 'Claude 3 Opus', speed: 'Medium', quality: 'Best', cost: '$$$', color: 'bg-orange-500' },
    { id: 'llama', name: 'Llama 3 (Local)', speed: 'Varies', quality: 'Good', cost: 'Free', color: 'bg-green-500' },
];

const SAMPLE_PROMPTS = [
    "Write a haiku about coding",
    "Explain quantum computing simply",
    "Create a recipe for banana bread",
];

export const ModelParameters = () => {
    const { completeModule } = useAcademyStore();
    const [selectedModel, setSelectedModel] = useState('gpt-4');
    const [temperature, setTemperature] = useState(0.7);
    const [maxTokens, setMaxTokens] = useState(500);
    const [topP, setTopP] = useState(1);
    const [prompt, setPrompt] = useState(SAMPLE_PROMPTS[0]);
    const [response, setResponse] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [experiments, setExperiments] = useState(0);
    const [completed, setCompleted] = useState(false);

    const generateResponse = () => {
        setIsGenerating(true);
        setResponse('');

        // Simulate different outputs based on parameters
        setTimeout(() => {
            let output = '';

            if (temperature < 0.3) {
                output = "🎯 [LOW CREATIVITY MODE]\n\nI provide precise, focused responses. My answers are consistent and predictable. Perfect for factual tasks.";
            } else if (temperature > 0.8) {
                output = "🎨 [HIGH CREATIVITY MODE]\n\nWhoa! Let me sprinkle some magic here... 🌟 My responses are wild, unexpected, and full of surprises! Each run gives you something totally different!";
            } else {
                output = "⚖️ [BALANCED MODE]\n\nI strike a nice balance between creativity and accuracy. Good for most general tasks where you want some variety but also reliability.";
            }

            if (maxTokens < 200) {
                output += "\n\n[Response truncated due to low max_tokens]";
            }

            const model = MODELS.find(m => m.id === selectedModel);
            output += `\n\n---\nModel: ${model?.name}\nTokens used: ~${Math.floor(maxTokens * 0.6)}\nTemperature: ${temperature}`;

            setResponse(output);
            setIsGenerating(false);
            setExperiments(e => e + 1);
        }, 1500);
    };

    // Completion check
    useEffect(() => {
        if (experiments >= 3 && !completed) {
            setCompleted(true);
            completeModule('model-parameters', 100);
        }
    }, [experiments, completed, completeModule]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-100 font-sans">
            {/* Header */}
            <div className="bg-white shadow-sm px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-purple-600">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Complete</div>}
                    <div className="bg-purple-100 text-purple-800 px-4 py-1 rounded-full text-sm font-bold">
                        🎛️ Model Parameters
                    </div>
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        <Sliders className="inline text-purple-600 mr-3" size={40} />
                        The AI Control Panel
                    </h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Different AI models have different strengths. <strong>Parameters</strong> let you fine-tune
                        how creative vs. precise the AI behaves!
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Model Selection */}
                    <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <Bot className="text-purple-500" /> Select Model
                        </h3>

                        <div className="space-y-2">
                            {MODELS.map(model => (
                                <button
                                    key={model.id}
                                    onClick={() => setSelectedModel(model.id)}
                                    className={`w-full p-4 rounded-xl text-left border-2 transition-all ${selectedModel === model.id
                                            ? 'border-purple-500 bg-purple-50'
                                            : 'border-slate-200 hover:border-purple-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 ${model.color} rounded-xl flex items-center justify-center`}>
                                            <Brain size={20} className="text-white" />
                                        </div>
                                        <div>
                                            <div className="font-bold text-slate-800">{model.name}</div>
                                            <div className="text-xs text-slate-500">
                                                {model.speed} • {model.quality} • {model.cost}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Parameters */}
                    <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                        <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                            <Sliders className="text-orange-500" /> Tune Parameters
                        </h3>

                        <div className="space-y-6">
                            {/* Temperature */}
                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="font-bold text-sm text-slate-600 flex items-center gap-1">
                                        <Thermometer size={14} /> Temperature
                                    </label>
                                    <span className="font-mono text-purple-600 font-bold">{temperature}</span>
                                </div>
                                <input
                                    type="range"
                                    min="0" max="1" step="0.1"
                                    value={temperature}
                                    onChange={e => setTemperature(parseFloat(e.target.value))}
                                    className="w-full accent-purple-500"
                                />
                                <div className="flex justify-between text-xs text-slate-500 mt-1">
                                    <span>🎯 Precise</span>
                                    <span>🎨 Creative</span>
                                </div>
                            </div>

                            {/* Max Tokens */}
                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="font-bold text-sm text-slate-600 flex items-center gap-1">
                                        <Zap size={14} /> Max Tokens
                                    </label>
                                    <span className="font-mono text-purple-600 font-bold">{maxTokens}</span>
                                </div>
                                <input
                                    type="range"
                                    min="50" max="2000" step="50"
                                    value={maxTokens}
                                    onChange={e => setMaxTokens(parseInt(e.target.value))}
                                    className="w-full accent-purple-500"
                                />
                                <div className="flex justify-between text-xs text-slate-500 mt-1">
                                    <span>Short</span>
                                    <span>Long</span>
                                </div>
                            </div>

                            {/* Top P */}
                            <div>
                                <div className="flex justify-between mb-2">
                                    <label className="font-bold text-sm text-slate-600">Top P (Nucleus)</label>
                                    <span className="font-mono text-purple-600 font-bold">{topP}</span>
                                </div>
                                <input
                                    type="range"
                                    min="0.1" max="1" step="0.1"
                                    value={topP}
                                    onChange={e => setTopP(parseFloat(e.target.value))}
                                    className="w-full accent-purple-500"
                                />
                            </div>
                        </div>

                        <button
                            onClick={generateResponse}
                            disabled={isGenerating}
                            className="w-full mt-6 bg-purple-600 text-white py-3 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {isGenerating ? <RotateCcw className="animate-spin" size={18} /> : <Zap size={18} />}
                            {isGenerating ? 'Generating...' : 'Test Configuration'}
                        </button>
                    </div>

                    {/* Output */}
                    <div className="bg-slate-900 rounded-3xl p-6 shadow-xl">
                        <h3 className="font-bold text-green-400 mb-4">AI Response</h3>

                        <div className="bg-slate-800 rounded-xl p-4 min-h-[300px] font-mono text-sm text-green-400 whitespace-pre-wrap">
                            {response || (
                                <span className="text-slate-600 italic">
                                    Configure parameters and click "Test Configuration" to see how different settings affect AI output...
                                </span>
                            )}
                        </div>

                        <div className="mt-4 text-slate-500 text-xs text-center">
                            💡 Experiment with 3 different configurations to complete!<br />
                            Experiments: {experiments}/3
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModelParameters;
